// =====================================================================
// @rehub/propertycut-timeline
// =====================================================================
// Single source of truth for PropertyCuts's video timeline.
//
//   buildTimeline(input, format) -> TimelineIR   (ordered scene list)
//   toShotstack(ir)              -> ShotstackEditPayload (final render)
//
// The worker compiles the IR to Shotstack JSON; the browser preview
// player renders the same IR onto a <canvas>. Because both sides consume
// one IR, the live preview matches the final cut.
//
// The Shotstack block builders below are ported verbatim from the old
// `apps/propertycut-worker/src/templates/blocks.ts` so that
// `toShotstack(buildTimeline(...))` produces byte-identical output to the
// previous `classicListingTemplate.build(...)`.
// =====================================================================

import type { ClipGrade } from './grade.ts';
import { motionClipProps, type ElementMotion } from './motion.ts';
import type { SpeedPiece } from './speed.ts';
import { estimateTextLines, fontStack, resolveTextStyle, TEXT_SHADOW, type TextStyle } from './text.ts';

// ---------------------------------------------------------------------
// Shared domain types (mirror apps/propertycut-worker/src/templates/types.ts)
// ---------------------------------------------------------------------

export type RenderFormat = 'vertical' | 'square' | 'landscape';
export type TemplateId = 'classic-listing' | 'social-reel' | 'square-highlight' | 'freeform';
export type AssetKind = 'photo' | 'video' | 'floor_plan';

/** Abstract transition style chosen per project; mapped to a Shotstack transition. */
export type TransitionStyle =
  | 'dissolve'
  | 'cut'
  | 'wipe'
  | 'slide'
  | 'zoom'
  | 'reveal'
  | 'carousel';

export interface TemplateAsset {
  url: string;
  kind: AssetKind;
  durationSeconds?: number | null;
  /** Video in-point in seconds (maps to Shotstack clip trim). */
  trimStartSeconds?: number | null;
  /** User-chosen play length in seconds; null/undefined = template default beat. */
  clipDurationSeconds?: number | null;
  width?: number | null;
  height?: number | null;
}

export interface TemplateListing {
  address?: string;
  price?: string;
  beds?: number;
  baths?: number;
  tenure?: 'freehold' | 'leasehold';
  description?: string;
}

/** How the logo animates onto its coloured bumper card. */
export type LogoAnimationStyle = 'fadeIn' | 'zoomIn' | 'slideUp' | 'reveal' | 'none';

export interface TemplateAgent {
  name?: string;
  phone?: string;
  email?: string;
  brandColor?: string;
  logoUrl?: string;
  /** Play an animated logo bumper before the video. */
  logoIntro?: boolean;
  /** Play an animated logo bumper after the video. */
  logoOutro?: boolean;
  /** Animation used for the logo bumper(s). Defaults to 'fadeIn'. */
  logoAnimation?: LogoAnimationStyle;
  /** Optional copy under the logo on the bumper. */
  logoTagline?: string;
}

export interface TemplateMusic {
  url: string;
  volume?: number;
}

export interface TemplateInput {
  listing: TemplateListing;
  assets: TemplateAsset[];
  agent: TemplateAgent;
  music?: TemplateMusic;
  /** Transition between clips. Defaults to 'dissolve' (crossfade) when absent. */
  transitionStyle?: TransitionStyle;
}

export interface ShotstackEditPayload {
  timeline: {
    background: string;
    tracks: Array<{ clips: any[] }>;
  };
  output: {
    format: 'mp4';
    resolution: 'hd';
    aspectRatio: '16:9' | '9:16' | '1:1';
    size: { width: number; height: number };
  };
}

export const FORMAT_DIMENSIONS: Record<
  RenderFormat,
  { width: number; height: number; aspectRatio: '16:9' | '9:16' | '1:1' }
> = {
  vertical: { width: 1080, height: 1920, aspectRatio: '9:16' },
  square: { width: 1080, height: 1080, aspectRatio: '1:1' },
  landscape: { width: 1920, height: 1080, aspectRatio: '16:9' },
};

/** Templates with a working timeline builder. */
export const SUPPORTED_TEMPLATE_IDS: TemplateId[] = ['classic-listing'];

export function isTemplateSupported(id: string): boolean {
  return (SUPPORTED_TEMPLATE_IDS as string[]).includes(id);
}

// ---------------------------------------------------------------------
// Timeline IR — the shared, render-engine-agnostic representation.
// ---------------------------------------------------------------------

export type SceneKind =
  | 'photo'
  | 'video'
  | 'floorPlan'
  | 'titleCard'
  | 'priceCard'
  | 'agentOutro'
  | 'logoBumper';

/** Resolved Shotstack transition object (already mapped from the abstract style). */
/** A join only sets the direction it actually plays. `in` is the start of this scene; `out` is its end. */
export type ResolvedTransition = { in?: string; out?: string } | undefined;

export interface BumperSpec {
  logoUrl: string;
  animation?: LogoAnimationStyle;
  tagline?: string;
  /** Fraction of the frame the logo fills. Classic bumpers leave this unset. */
  scale?: number;
  /** Fade the card itself in/out so it doesn't hard-cut the footage. */
  fadeCard?: { in?: boolean; out?: boolean };
  /** Freeform plates: replaces `animation` with keyframed motion. */
  motion?: ElementMotion;
  /** Logo centre as a fraction of the frame (0–1). Defaults to the middle. */
  x?: number;
  y?: number;
  /** More logos on the same plate, sharing this bumper's motion. Each needs its own Shotstack track. */
  extraLogos?: Array<{ logoUrl: string; scale?: number; x?: number; y?: number }>;
}

/**
 * A single beat on the main track. Carries everything both renderers
 * need: the Shotstack-resolved transition, the media source, and the
 * structured text for cards.
 */
export interface TimelineScene {
  id: string;
  kind: SceneKind;
  /** Seconds from the start of the timeline. */
  start: number;
  /** Seconds this scene plays. */
  length: number;
  transition?: ResolvedTransition;

  // media (photo / video / floorPlan)
  src?: string;
  fit?: 'cover' | 'contain';
  /** Ken-Burns direction for photos. */
  kenBurns?: 'in' | 'out';
  /** Video in-point in seconds. */
  trim?: number;

  // cards
  brandColor?: string;
  /** titleCard */
  title?: string;
  /** titleCard */
  subtitle?: string;
  /** priceCard */
  listing?: TemplateListing;
  /** agentOutro */
  agent?: TemplateAgent;

  // logoBumper
  bumper?: BumperSpec;
  /** Freeform colour plate from the editor. Classic bumpers leave this unset. */
  plate?: boolean;

  /**
   * Crop shift for freeform clips, -1..1 from centre. Positive x moves the
   * media right; positive y moves it up (Shotstack offset space).
   */
  offset?: { x: number; y: number };
  /** Freeform clips shrunk within the frame (always `contain`). Omitted = full frame. */
  scale?: number;
  /** Original soundtrack level for video clips. Omitted = muted, matching classic templates. */
  volume?: number;
  /**
   * Curves and white balance for a video scene. The preview paints this.
   * The worker bakes it into the file and removes it before Shotstack.
   */
  grade?: ClipGrade;
  /**
   * Video played at changing speed: consecutive constant-speed pieces of
   * source footage, from `trim`, that together last `length`. Sound is
   * muted wherever the speed is not 1x. Omitted = normal speed.
   */
  speed?: SpeedPiece[];
}

export interface TimelineTextOverlay {
  text: string;
  start: number;
  length: number;
  /** Pixels at a 1080px-tall canvas; scaled to the output height at render time. */
  fontSize: number;
  color: string;
  align: 'left' | 'center' | 'right';
  background: string | null;
  /** 0–1, origin top-left of the frame. */
  x: number;
  y: number;
  motion?: ElementMotion;
  style?: TextStyle;
}

export interface TimelineAudioClip {
  src: string;
  start: number;
  length: number;
  trim: number;
  /** Already limited so overlapping sources do not sum above the source levels. */
  volume: number;
  fadeIn: number;
  fadeOut: number;
}

export interface TimelineLogoOverlay {
  src: string;
  start: number;
  length: number;
  x: number;
  y: number;
  scale: number;
  motion?: ElementMotion;
  /** Stretches where the logo shows. Omitted = the whole `start`/`length` range. */
  spans?: Array<{ start: number; length: number }>;
}

/** Where a logo overlay is on screen, one entry per continuous stretch. */
/**
 * Shotstack's built-in audio fade effects always last 2s, so fades are written as volume keyframes
 * (times relative to the clip) to honour the chosen lengths. Returns a plain number when there's no fade.
 */
export function audioVolumeTweens(
  volume: number,
  length: number,
  fadeIn: number,
  fadeOut: number,
): number | Array<{ from: number; to: number; start: number; length: number; interpolation: 'linear' }> {
  const fi = Math.max(0, Math.min(fadeIn, length));
  const fo = Math.max(0, Math.min(fadeOut, length - fi));
  if (fi <= 0 && fo <= 0) return volume;
  const r = (n: number) => Math.round(n * 1000) / 1000;
  const tweens: Array<{ from: number; to: number; start: number; length: number; interpolation: 'linear' }> = [];
  if (fi > 0) tweens.push({ from: 0, to: volume, start: 0, length: r(fi), interpolation: 'linear' });
  const hold = length - fi - fo;
  if (hold > 0.001) tweens.push({ from: volume, to: volume, start: r(fi), length: r(hold), interpolation: 'linear' });
  if (fo > 0) tweens.push({ from: volume, to: 0, start: r(length - fo), length: r(fo), interpolation: 'linear' });
  return tweens;
}

export function logoOverlaySpans(logo: TimelineLogoOverlay): Array<{ start: number; length: number }> {
  return logo.spans ?? [{ start: logo.start, length: logo.length }];
}

/** A logo image overlaid on its own track (watermark during interiors). */
export interface TimelineWatermark {
  src: string;
  start: number;
  length: number;
  scale: number;
  position: 'center' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
}

export interface TimelineMusic {
  src: string;
  volume: number;
}

export interface TimelineIR {
  templateId: TemplateId;
  format: RenderFormat;
  width: number;
  height: number;
  aspectRatio: '16:9' | '9:16' | '1:1';
  background: string;
  totalLength: number;
  scenes: TimelineScene[];
  watermark?: TimelineWatermark;
  /** Single bed used by classic templates. Freeform edits use `audioClips` instead. */
  music?: TimelineMusic;
  texts?: TimelineTextOverlay[];
  audioClips?: TimelineAudioClip[];
  logoOverlay?: TimelineLogoOverlay;
}

// =====================================================================
// Shotstack block builders (ported verbatim from blocks.ts)
// =====================================================================

const TRANSITION_MAP: Record<TransitionStyle, ResolvedTransition> = {
  dissolve: { in: 'fade', out: 'fade' },
  cut: undefined,
  wipe: { in: 'wipeRight', out: 'wipeRight' },
  // Whole-frame pushes. Shotstack's slide* effect only nudges and fades,
  // and opposite in/out directions cancel into something like a dissolve.
  slide: { in: 'carouselLeft', out: 'carouselLeft' },
  zoom: { in: 'zoom', out: 'zoom' },
  reveal: { in: 'reveal', out: 'reveal' },
  carousel: { in: 'carouselUp', out: 'carouselUp' },
};

/** Resolve a transition object for a style (defaults to dissolve/fade). */
export function transitionFor(style: TransitionStyle | undefined): ResolvedTransition {
  if (!style) return TRANSITION_MAP.dissolve;
  return TRANSITION_MAP[style] ?? TRANSITION_MAP.dissolve;
}

/** Spread helper: yields `{ transition }` only when one is defined. */
function withTransition(transition: ResolvedTransition): { transition?: { in?: string; out?: string } } {
  const join = definedJoin(transition);
  return join ? { transition: join } : {};
}

/** Drop empty directions so Shotstack does not fade a clip that should cut. */
function definedJoin(transition: ResolvedTransition): { in?: string; out?: string } | undefined {
  if (!transition) return undefined;
  const join: { in?: string; out?: string } = {};
  if (transition.in) join.in = transition.in;
  if (transition.out) join.out = transition.out;
  return join.in || join.out ? join : undefined;
}

/** Keep a plate logo on screen for exactly as long as the colour behind it. */
function withJoinOut(clip: any, transition: ResolvedTransition): any {
  const out = transition?.out;
  if (!out) return clip;
  return { ...clip, transition: { ...(clip.transition ?? {}), out } };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Use safe inline styles only — Shotstack's HTML renderer is conservative.
const FONT_STACK = 'Helvetica, Arial, sans-serif';

interface TitleCardOpts {
  title: string;
  subtitle?: string;
  width: number;
  height: number;
  brandColor: string;
  length: number;
  transition?: ResolvedTransition;
}

function buildTitleCardClip(opts: TitleCardOpts): any {
  const titleFont = Math.round(opts.width * 0.045);
  const subFont = Math.round(opts.width * 0.022);
  const sub = opts.subtitle
    ? `<p style="color: rgba(255,255,255,0.78); font-size: ${subFont}px; font-family: ${FONT_STACK}; font-weight: 300; letter-spacing: 0.18em; text-transform: uppercase; margin-top: ${Math.round(opts.height * 0.02)}px;">${escapeHtml(opts.subtitle)}</p>`
    : '';

  const html =
    `<table width="${opts.width}" height="${opts.height}" cellpadding="0" cellspacing="0">` +
    `<tr><td valign="middle" align="center" style="padding: 0 ${Math.round(opts.width * 0.08)}px;">` +
    `<p style="color: #ffffff; font-size: ${titleFont}px; font-family: ${FONT_STACK}; font-weight: 300; line-height: 1.15; margin: 0;">${escapeHtml(opts.title)}</p>` +
    sub +
    `</td></tr></table>`;

  return {
    asset: {
      type: 'html',
      html,
      width: opts.width,
      height: opts.height,
      background: opts.brandColor,
    },
    length: opts.length,
    ...withTransition(opts.transition),
  };
}

interface KenBurnsOpts {
  url: string;
  length: number;
  direction?: 'in' | 'out';
  transition?: ResolvedTransition;
}

function buildKenBurnsClip(opts: KenBurnsOpts): any {
  const effect = opts.direction === 'out' ? 'zoomOut' : 'zoomIn';
  return {
    asset: { type: 'image', src: opts.url },
    length: opts.length,
    fit: 'cover',
    effect,
    ...withTransition(opts.transition),
  };
}

interface VideoClipOpts {
  url: string;
  length: number;
  trim?: number;
  transition?: ResolvedTransition;
  /** Undefined keeps the classic-template default of muted original sound. */
  volume?: number;
  fit?: 'cover' | 'contain';
  offset?: { x: number; y: number };
  scale?: number;
}

function buildVideoClip(opts: VideoClipOpts): any {
  const asset: Record<string, any> = { type: 'video', src: opts.url, volume: opts.volume ?? 0 };
  if (opts.trim && opts.trim > 0) asset.trim = opts.trim;
  const clip: Record<string, any> = {
    asset,
    length: opts.length,
    fit: opts.fit ?? 'cover',
    ...withTransition(opts.transition),
  };
  if (opts.offset && (opts.offset.x !== 0 || opts.offset.y !== 0)) clip.offset = opts.offset;
  if (opts.scale != null && opts.scale < 1) clip.scale = opts.scale;
  return clip;
}

/**
 * One Shotstack clip per constant-speed piece, back to back. The scene's
 * transition goes on the first piece's way in and the last piece's way out.
 */
function buildSpeedRampClips(scene: TimelineScene): any[] {
  const pieces = scene.speed ?? [];
  const end = scene.start + scene.length;
  const at = (n: number) => Math.round(n * 1000) / 1000;
  const clips: any[] = [];
  let cursor = scene.start;
  pieces.forEach((piece, i) => {
    const last = i === pieces.length - 1;
    const start = at(cursor);
    cursor += (piece.to - piece.from) / piece.speed;
    const stop = last ? at(end) : at(Math.min(cursor, end));
    if (stop - start < 0.001) return;
    const clip = buildVideoClip({
      url: scene.src!,
      length: at(stop - start),
      // Shotstack measures `trim` on the sped-up video, so source second
      // `from` at speed s is trim `from / s`.
      trim: at(piece.from / piece.speed),
      volume: piece.speed === 1 ? scene.volume : 0,
      fit: scene.fit,
      offset: scene.offset,
      scale: scene.scale,
    });
    if (piece.speed !== 1) clip.asset.speed = Math.round(piece.speed * 1000) / 1000;
    clips.push({ ...clip, start });
  });
  if (scene.transition && clips.length) {
    if (scene.transition.in) clips[0].transition = { ...(clips[0].transition ?? {}), in: scene.transition.in };
    if (scene.transition.out) {
      const tail = clips[clips.length - 1];
      tail.transition = { ...(tail.transition ?? {}), out: scene.transition.out };
    }
  }
  return clips;
}

function buildFloorPlanClip(opts: {
  url: string;
  length: number;
  brandColor: string;
  transition?: ResolvedTransition;
}): any {
  return {
    asset: { type: 'image', src: opts.url },
    length: opts.length,
    fit: 'contain',
    ...withTransition(opts.transition),
  };
}

interface PriceCardOpts {
  listing: TemplateListing;
  width: number;
  height: number;
  brandColor: string;
  length: number;
  transition?: ResolvedTransition;
}

function buildPriceCardClip(opts: PriceCardOpts): any {
  const lines: string[] = [];
  if (opts.listing.price) {
    lines.push(
      `<p style="color: #ffffff; font-size: ${Math.round(opts.width * 0.06)}px; font-family: ${FONT_STACK}; font-weight: 300; margin: 0;">${escapeHtml(opts.listing.price)}</p>`,
    );
  }

  const stats: string[] = [];
  if (opts.listing.beds != null) {
    stats.push(`${opts.listing.beds} bed${opts.listing.beds === 1 ? '' : 's'}`);
  }
  if (opts.listing.baths != null) {
    stats.push(`${opts.listing.baths} bath${opts.listing.baths === 1 ? '' : 's'}`);
  }
  if (opts.listing.tenure) {
    stats.push(opts.listing.tenure);
  }
  if (stats.length > 0) {
    lines.push(
      `<p style="color: rgba(255,255,255,0.82); font-size: ${Math.round(opts.width * 0.022)}px; font-family: ${FONT_STACK}; font-weight: 300; letter-spacing: 0.18em; text-transform: uppercase; margin-top: ${Math.round(opts.height * 0.025)}px;">${escapeHtml(stats.join('  ·  '))}</p>`,
    );
  }

  if (opts.listing.address) {
    lines.push(
      `<p style="color: rgba(255,255,255,0.7); font-size: ${Math.round(opts.width * 0.02)}px; font-family: ${FONT_STACK}; font-weight: 300; margin-top: ${Math.round(opts.height * 0.04)}px;">${escapeHtml(opts.listing.address)}</p>`,
    );
  }

  const html =
    `<table width="${opts.width}" height="${opts.height}" cellpadding="0" cellspacing="0">` +
    `<tr><td valign="middle" align="center" style="padding: 0 ${Math.round(opts.width * 0.08)}px;">${lines.join('')}</td></tr></table>`;

  return {
    asset: {
      type: 'html',
      html,
      width: opts.width,
      height: opts.height,
      background: opts.brandColor,
    },
    length: opts.length,
    ...withTransition(opts.transition),
  };
}

interface AgentOutroOpts {
  agent: TemplateAgent;
  width: number;
  height: number;
  brandColor: string;
  length: number;
  transition?: ResolvedTransition;
}

function buildAgentOutroClip(opts: AgentOutroOpts): any {
  const contactLines: string[] = [];
  if (opts.agent.phone) contactLines.push(escapeHtml(opts.agent.phone));
  if (opts.agent.email) contactLines.push(escapeHtml(opts.agent.email));
  const contact = contactLines.length
    ? `<p style="color: rgba(255,255,255,0.78); font-size: ${Math.round(opts.width * 0.022)}px; font-family: ${FONT_STACK}; font-weight: 300; margin-top: ${Math.round(opts.height * 0.04)}px; letter-spacing: 0.06em;">${contactLines.join(' &nbsp;·&nbsp; ')}</p>`
    : '';

  const name = opts.agent.name
    ? `<p style="color: #ffffff; font-size: ${Math.round(opts.width * 0.05)}px; font-family: ${FONT_STACK}; font-weight: 300; margin: 0;">${escapeHtml(opts.agent.name)}</p>`
    : '';

  const html =
    `<table width="${opts.width}" height="${opts.height}" cellpadding="0" cellspacing="0">` +
    `<tr><td valign="middle" align="center" style="padding: 0 ${Math.round(opts.width * 0.08)}px;">${name}${contact}</td></tr></table>`;

  return {
    asset: {
      type: 'html',
      html,
      width: opts.width,
      height: opts.height,
      background: opts.brandColor,
    },
    length: opts.length,
    ...withTransition(opts.transition),
  };
}

function buildLogoOverlay(opts: {
  logoUrl: string;
  start: number;
  length: number;
  scale?: number;
  position?: 'center' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
}): any {
  return {
    asset: { type: 'image', src: opts.logoUrl },
    start: opts.start,
    length: opts.length,
    fit: 'contain',
    position: opts.position ?? 'center',
    scale: opts.scale ?? 0.35,
  };
}

/** Map the user's logo animation style to a Shotstack `in` transition. */
function bumperTransitionFor(style: LogoAnimationStyle | undefined): string | undefined {
  if (style === 'none') return undefined;
  const map: Record<Exclude<LogoAnimationStyle, 'none'>, string> = {
    fadeIn: 'fade',
    zoomIn: 'zoom',
    slideUp: 'slideUp',
    reveal: 'reveal',
  };
  return map[(style ?? 'fadeIn') as Exclude<LogoAnimationStyle, 'none'>] ?? 'fade';
}

interface LogoBumperOpts {
  logoUrl: string;
  start: number;
  length: number;
  width: number;
  height: number;
  brandColor: string;
  animation?: LogoAnimationStyle;
  tagline?: string;
  scale?: number;
  fadeCard?: { in?: boolean; out?: boolean };
  motion?: ElementMotion;
  x?: number;
  y?: number;
}

interface LogoBumperClips {
  background: any;
  overlays: any[];
}

function plateLogoClip(opts: {
  logoUrl: string;
  start: number;
  length: number;
  scale?: number;
  motion?: ElementMotion;
  x?: number;
  y?: number;
  animation?: LogoAnimationStyle;
  fadeOut?: boolean;
  /** Shift used when a tagline sits under the logo. */
  nudgeY?: number;
}): any {
  const animIn = bumperTransitionFor(opts.animation);
  const logoTransition: { in?: string; out?: string } = {};
  if (animIn) logoTransition.in = animIn;
  if (opts.fadeOut) logoTransition.out = 'fade';
  const logoScale = Math.max(0.05, Math.min(1.5, opts.scale ?? 0.68));
  const logoX = clampUnit((opts.x ?? 0.5) - 0.5);
  const logoY = clampUnit(0.5 - (opts.y ?? 0.5)) + (opts.nudgeY ?? 0);
  if (opts.motion) {
    return {
      asset: { type: 'image', src: opts.logoUrl },
      start: opts.start,
      length: opts.length,
      fit: 'contain',
      position: 'center',
      ...motionClipProps(opts.motion, opts.length, { x: logoX, y: logoY, scale: logoScale }),
    };
  }
  return {
    asset: { type: 'image', src: opts.logoUrl },
    start: opts.start,
    length: opts.length,
    fit: 'contain',
    position: 'center',
    scale: logoScale,
    ...(Object.keys(logoTransition).length > 0 ? { transition: logoTransition } : {}),
    ...(logoX || logoY ? { offset: { x: logoX, y: logoY } } : {}),
  };
}

function buildLogoBumper(opts: LogoBumperOpts): LogoBumperClips {
  const cardTransition: { in?: string; out?: string } = {};
  if (opts.fadeCard?.in) cardTransition.in = 'fade';
  if (opts.fadeCard?.out) cardTransition.out = 'fade';

  const background = {
    asset: { type: 'html', html: '<p>&nbsp;</p>', width: opts.width, height: opts.height, background: opts.brandColor },
    start: opts.start,
    length: opts.length,
    ...(Object.keys(cardTransition).length > 0 ? { transition: cardTransition } : {}),
  };

  const logoClip = plateLogoClip({
    logoUrl: opts.logoUrl,
    start: opts.start,
    length: opts.length,
    scale: opts.scale,
    motion: opts.motion,
    x: opts.x,
    y: opts.y,
    animation: opts.motion ? undefined : opts.animation,
    fadeOut: opts.fadeCard?.out,
    nudgeY: opts.tagline ? 0.05 : 0,
  });

  const overlays: any[] = [logoClip];

  if (opts.tagline) {
    const tagFont = Math.round(opts.width * 0.02);
    const html =
      `<table width="${opts.width}" height="${opts.height}" cellpadding="0" cellspacing="0">` +
      `<tr><td valign="middle" align="center" style="padding-top: ${Math.round(opts.height * 0.22)}px;">` +
      `<p style="color: rgba(255,255,255,0.82); font-size: ${tagFont}px; font-family: ${FONT_STACK}; font-weight: 300; letter-spacing: 0.12em; margin: 0;">${escapeHtml(opts.tagline)}</p>` +
      `</td></tr></table>`;
    const animIn = bumperTransitionFor(opts.animation);
    const tagTransition: { in?: string; out?: string } = {};
    if (animIn) tagTransition.in = animIn;
    if (opts.fadeCard?.out) tagTransition.out = 'fade';
    overlays.push({
      asset: { type: 'html', html, width: opts.width, height: opts.height },
      start: opts.start,
      length: opts.length,
      ...(Object.keys(tagTransition).length > 0 ? { transition: tagTransition } : {}),
    });
  }

  return { background, overlays };
}

// =====================================================================
// buildTimeline — Classic Listing layout -> TimelineIR
// =====================================================================
// Ported from apps/propertycut-worker/src/templates/classic-listing.ts.
// The same beat layout serves all three formats; only the canvas size and
// HTML font-sizing (handled in toShotstack) shift per format.
// =====================================================================

const DEFAULT_BG = '#0f172a'; // slate-900 — sober, brand-neutral fallback

// Beat lengths (seconds). User per-clip overrides take precedence.
const BEATS = {
  logoBumper: 3,
  titleCard: 3,
  exterior: 4,
  interior: 4,
  floorPlan: 4,
  priceCard: 4,
  agentOutro: 4,
};

function pickExterior(assets: TemplateAsset[]): TemplateAsset | null {
  const photos = assets.filter((a) => a.kind === 'photo');
  return photos[0] ?? null;
}

function pickInteriors(assets: TemplateAsset[], exterior: TemplateAsset | null): TemplateAsset[] {
  return assets.filter((a) => a.kind !== 'floor_plan' && a !== exterior);
}

function pickFloorPlan(assets: TemplateAsset[]): TemplateAsset | null {
  return assets.find((a) => a.kind === 'floor_plan') ?? null;
}

/**
 * How long a single asset plays. Honors the user's per-clip
 * `clipDurationSeconds` override; otherwise falls back to the template's
 * default beat. For videos, the trimmed segment is clamped to what's
 * actually available after the in-point so we never run past the source.
 */
function effectiveLength(asset: TemplateAsset, beatDefault: number): number {
  const chosen =
    asset.clipDurationSeconds != null && asset.clipDurationSeconds > 0
      ? asset.clipDurationSeconds
      : beatDefault;
  if (asset.kind === 'video' && asset.durationSeconds && asset.durationSeconds > 0) {
    const trim = asset.trimStartSeconds ?? 0;
    const available = asset.durationSeconds - trim;
    if (available > 0) return Math.min(chosen, available);
  }
  return chosen;
}

export function buildTimeline(input: TemplateInput, format: RenderFormat): TimelineIR {
  const dims = FORMAT_DIMENSIONS[format];
  const brandColor = input.agent.brandColor || DEFAULT_BG;
  const transition = transitionFor(input.transitionStyle);

  const exterior = pickExterior(input.assets);
  const interiors = pickInteriors(input.assets, exterior);
  const floorPlan = pickFloorPlan(input.assets);

  const title = input.listing.address ?? 'Property tour';
  const subtitle = input.listing.price ? input.listing.price : undefined;

  const scenes: TimelineScene[] = [];
  let t = 0;
  let sceneSeq = 0;
  const nextId = (prefix: string) => `${prefix}-${sceneSeq++}`;

  const hasLogo = Boolean(input.agent.logoUrl);
  const wantIntroBumper = hasLogo && input.agent.logoIntro === true;
  const wantOutroBumper = hasLogo && input.agent.logoOutro === true;

  // 0. Intro logo bumper
  if (wantIntroBumper) {
    scenes.push({
      id: nextId('logo-intro'),
      kind: 'logoBumper',
      start: t,
      length: BEATS.logoBumper,
      brandColor,
      bumper: {
        logoUrl: input.agent.logoUrl!,
        animation: input.agent.logoAnimation,
        tagline: input.agent.logoTagline,
        fadeCard: { out: true },
      },
    });
    t += BEATS.logoBumper;
  }

  // 1. Title card
  scenes.push({
    id: nextId('title'),
    kind: 'titleCard',
    start: t,
    length: BEATS.titleCard,
    brandColor,
    title,
    subtitle,
    transition,
  });
  t += BEATS.titleCard;

  // 2. Exterior beat (Ken-Burns zoom-in)
  if (exterior) {
    const exteriorLength = effectiveLength(exterior, BEATS.exterior);
    scenes.push({
      id: nextId('exterior'),
      kind: 'photo',
      start: t,
      length: exteriorLength,
      src: exterior.url,
      fit: 'cover',
      kenBurns: 'in',
      transition,
    });
    t += exteriorLength;
  }

  // 3. Interior beats — each user clip plays once, in the arranged order.
  const interiorStart = t;
  for (let i = 0; i < interiors.length; i++) {
    const asset = interiors[i];
    const length = effectiveLength(asset, BEATS.interior);
    if (asset.kind === 'video') {
      scenes.push({
        id: nextId('clip'),
        kind: 'video',
        start: t,
        length,
        src: asset.url,
        fit: 'cover',
        trim: asset.trimStartSeconds ?? 0,
        transition,
      });
    } else {
      scenes.push({
        id: nextId('clip'),
        kind: 'photo',
        start: t,
        length,
        src: asset.url,
        fit: 'cover',
        kenBurns: i % 2 === 0 ? 'in' : 'out',
        transition,
      });
    }
    t += length;
  }
  const interiorLength = t - interiorStart;

  // 4. Floor plan reveal
  if (floorPlan) {
    const floorPlanLength = effectiveLength(floorPlan, BEATS.floorPlan);
    scenes.push({
      id: nextId('floorplan'),
      kind: 'floorPlan',
      start: t,
      length: floorPlanLength,
      src: floorPlan.url,
      fit: 'contain',
      brandColor,
      transition,
    });
    t += floorPlanLength;
  }

  // 5. Price / details card
  scenes.push({
    id: nextId('price'),
    kind: 'priceCard',
    start: t,
    length: BEATS.priceCard,
    brandColor,
    listing: input.listing,
    transition,
  });
  t += BEATS.priceCard;

  // 6. Agent outro
  if (input.agent.name || input.agent.phone || input.agent.email) {
    scenes.push({
      id: nextId('agent'),
      kind: 'agentOutro',
      start: t,
      length: BEATS.agentOutro,
      brandColor,
      agent: input.agent,
      transition,
    });
    t += BEATS.agentOutro;
  }

  // 7. Outro logo bumper
  if (wantOutroBumper) {
    scenes.push({
      id: nextId('logo-outro'),
      kind: 'logoBumper',
      start: t,
      length: BEATS.logoBumper,
      brandColor,
      bumper: {
        logoUrl: input.agent.logoUrl!,
        animation: input.agent.logoAnimation,
        tagline: input.agent.logoTagline,
        fadeCard: { in: true },
      },
    });
    t += BEATS.logoBumper;
  }

  const totalLength = t;

  // Logo watermark during interior beats only.
  let watermark: TimelineWatermark | undefined;
  if (input.agent.logoUrl && interiorLength > 0) {
    watermark = {
      src: input.agent.logoUrl,
      start: interiorStart,
      length: interiorLength,
      scale: 0.12,
      position: 'bottomRight',
    };
  }

  const music: TimelineMusic | undefined = input.music?.url
    ? { src: input.music.url, volume: input.music.volume ?? 0.7 }
    : undefined;

  return {
    templateId: 'classic-listing',
    format,
    width: dims.width,
    height: dims.height,
    aspectRatio: dims.aspectRatio,
    background: brandColor,
    totalLength,
    scenes,
    watermark,
    music,
  };
}

// =====================================================================
// toShotstack — TimelineIR -> Shotstack edit payload
// =====================================================================
// Produces byte-identical output to the previous classic-listing builder.
// =====================================================================

function clampUnit(n: number): number {
  if (Number.isNaN(n)) return 0;
  return Math.max(-1, Math.min(1, n));
}

/** Still image without Ken Burns — used by the freeform editor. */
function buildFreeStillClip(scene: TimelineScene): any {
  const clip: Record<string, any> = {
    asset: { type: 'image', src: scene.src },
    start: scene.start,
    length: scene.length,
    fit: scene.fit ?? 'cover',
    ...withTransition(scene.transition),
  };
  if (scene.offset && (scene.offset.x !== 0 || scene.offset.y !== 0)) {
    clip.offset = { x: clampUnit(scene.offset.x), y: clampUnit(scene.offset.y) };
  }
  if (scene.scale != null && scene.scale < 1) clip.scale = scene.scale;
  return clip;
}

function buildFreeTextClip(text: TimelineTextOverlay, width: number, height: number): any {
  const style = text.style ?? resolveTextStyle({});
  const font = Math.max(16, Math.round(text.fontSize * (height / 1080)));
  const content = style.uppercase ? text.text.toUpperCase() : text.text;
  const boxW = Math.round(width * style.width);
  const padY = text.background ? Math.round(font * 0.25) : 0;
  const padX = text.background ? Math.round(font * 0.5) : 0;
  const lines = estimateTextLines(content, font, boxW - padX * 2, style);
  const boxH = Math.max(
    Math.round(font * 2.4),
    Math.round(height * 0.12),
    Math.round(lines * font * style.lineHeight + padY * 2 + font * 0.6),
  );
  const css = [
    `color:${text.color}`,
    `font-size:${font}px`,
    `font-family:${fontStack(style.fontFamily)}`,
    `font-weight:${style.fontWeight}`,
    `font-style:${style.italic ? 'italic' : 'normal'}`,
    `letter-spacing:${style.letterSpacing}em`,
    `line-height:${style.lineHeight}`,
    `text-align:${text.align}`,
    'margin:0',
    style.shadow ? `text-shadow:${TEXT_SHADOW}` : '',
    text.background
      ? `background:${text.background};padding:${padY}px ${padX}px;border-radius:${Math.round(font * 0.25)}px`
      : '',
  ].filter(Boolean).join(';');
  const html =
    `<div style="width:${boxW}px;height:${boxH}px;display:flex;align-items:center;justify-content:${
      text.align === 'left' ? 'flex-start' : text.align === 'right' ? 'flex-end' : 'center'
    };">` +
    `<p style="${css}">${escapeHtml(content).replace(/\n/g, '<br>')}</p></div>`;
  return {
    asset: {
      type: 'html',
      html,
      width: boxW,
      height: boxH,
      background: 'transparent',
    },
    start: text.start,
    length: text.length,
    position: 'center',
    offset: {
      x: clampUnit(text.x - 0.5),
      y: clampUnit(0.5 - text.y),
    },
    ...motionClipProps(text.motion, text.length, {
      x: clampUnit(text.x - 0.5),
      y: clampUnit(0.5 - text.y),
      opacity: style.opacity,
    }),
  };
}

/**
 * Keep a single visual track when clips only touch end-to-end. When a clip
 * starts before the previous one ends (a crossfade), alternate tracks so
 * Shotstack can composite the overlap instead of cutting it.
 */
function packVisualTracks(clips: any[]): Array<{ clips: any[] }> {
  let overlaps = false;
  for (let i = 1; i < clips.length; i++) {
    const prev = clips[i - 1];
    const start = clips[i].start ?? 0;
    const prevEnd = (prev.start ?? 0) + (prev.length ?? 0);
    if (start < prevEnd - 0.02) {
      overlaps = true;
      break;
    }
  }
  if (!overlaps) return [{ clips }];
  const even: any[] = [];
  const odd: any[] = [];
  clips.forEach((clip, i) => (i % 2 === 0 ? even : odd).push(clip));
  const tracks: Array<{ clips: any[] }> = [];
  if (even.length) tracks.push({ clips: even });
  if (odd.length) tracks.push({ clips: odd });
  return tracks;
}

export function toShotstack(ir: TimelineIR): ShotstackEditPayload {
  const width = ir.width;
  const height = ir.height;
  const brandColor = ir.background;

  const mainClips: any[] = [];
  const logoTrackClips: any[] = [];
  const partnerLogoClips: any[] = [];

  for (const scene of ir.scenes) {
    switch (scene.kind) {
      case 'logoBumper': {
        if (scene.plate) {
          const plateColor = scene.brandColor || DEFAULT_BG;
          const extras = scene.bumper?.extraLogos ?? [];
          if (scene.bumper?.logoUrl) {
            const bumper = buildLogoBumper({
              logoUrl: scene.bumper.logoUrl,
              start: scene.start,
              length: scene.length,
              width,
              height,
              brandColor: plateColor,
              animation: scene.bumper.animation,
              tagline: scene.bumper.tagline,
              scale: scene.bumper.scale,
              fadeCard: scene.bumper.fadeCard,
              motion: scene.bumper.motion,
              x: scene.bumper.x,
              y: scene.bumper.y,
            });
            const background = { ...bumper.background };
            const join = definedJoin(scene.transition);
            if (join) background.transition = { ...(background.transition ?? {}), ...join };
            mainClips.push(background);
            // The logo sits on its own track. It has to leave with the plate colour,
            // or the colour fades out and the logo is left on the canvas.
            logoTrackClips.push(...bumper.overlays.map((clip) => withJoinOut(clip, scene.transition)));
          } else {
            mainClips.push({
              asset: {
                type: 'html',
                html: '<p>&nbsp;</p>',
                width,
                height,
                background: plateColor,
              },
              start: scene.start,
              length: scene.length,
              ...withTransition(scene.transition),
            });
          }
          for (const extra of extras) {
            partnerLogoClips.push(withJoinOut(plateLogoClip({
              logoUrl: extra.logoUrl,
              start: scene.start,
              length: scene.length,
              scale: extra.scale,
              motion: scene.bumper?.motion,
              x: extra.x,
              y: extra.y,
              animation: scene.bumper?.motion ? undefined : scene.bumper?.animation,
              fadeOut: scene.bumper?.fadeCard?.out,
            }), scene.transition));
          }
          break;
        }
        const bumper = buildLogoBumper({
          logoUrl: scene.bumper!.logoUrl,
          start: scene.start,
          length: scene.length,
          width,
          height,
          brandColor,
          animation: scene.bumper!.animation,
          tagline: scene.bumper!.tagline,
          scale: scene.bumper!.scale,
          fadeCard: scene.bumper!.fadeCard,
        });
        mainClips.push(bumper.background);
        logoTrackClips.push(...bumper.overlays);
        break;
      }
      case 'titleCard':
        mainClips.push({
          ...buildTitleCardClip({
            title: scene.title ?? '',
            subtitle: scene.subtitle,
            width,
            height,
            brandColor,
            length: scene.length,
            transition: scene.transition,
          }),
          start: scene.start,
        });
        break;
      case 'photo':
        if (scene.kenBurns) {
          mainClips.push({
            ...buildKenBurnsClip({
              url: scene.src!,
              length: scene.length,
              direction: scene.kenBurns,
              transition: scene.transition,
            }),
            start: scene.start,
          });
        } else {
          mainClips.push(buildFreeStillClip(scene));
        }
        break;
      case 'video':
        if (scene.speed?.length) {
          mainClips.push(...buildSpeedRampClips(scene));
          break;
        }
        mainClips.push({
          ...buildVideoClip({
            url: scene.src!,
            length: scene.length,
            trim: scene.trim ?? 0,
            transition: scene.transition,
            volume: scene.volume,
            fit: scene.fit,
            offset: scene.offset,
            scale: scene.scale,
          }),
          start: scene.start,
        });
        break;
      case 'floorPlan':
        if (scene.offset || scene.fit === 'cover' || scene.scale != null) {
          mainClips.push(buildFreeStillClip(scene));
        } else {
          mainClips.push({
            ...buildFloorPlanClip({
              url: scene.src!,
              length: scene.length,
              brandColor,
              transition: scene.transition,
            }),
            start: scene.start,
          });
        }
        break;
      case 'priceCard':
        mainClips.push({
          ...buildPriceCardClip({
            listing: scene.listing ?? {},
            width,
            height,
            brandColor,
            length: scene.length,
            transition: scene.transition,
          }),
          start: scene.start,
        });
        break;
      case 'agentOutro':
        mainClips.push({
          ...buildAgentOutroClip({
            agent: scene.agent ?? {},
            width,
            height,
            brandColor,
            length: scene.length,
            transition: scene.transition,
          }),
          start: scene.start,
        });
        break;
    }
  }

  // Build tracks. Shotstack draws the first track on top, so overlays go
  // first: text, then the corner logo, then plate logos and the watermark,
  // then the full-frame footage and cards underneath. Overlapping freeform
  // clips (a crossfade) alternate visual tracks — clips on one Shotstack
  // track cannot blend.
  const tracks: Array<{ clips: any[] }> = [];

  if (ir.texts && ir.texts.length > 0) {
    for (const text of [...ir.texts].reverse()) {
      if (!text.text.trim()) continue;
      tracks.push({ clips: [buildFreeTextClip(text, width, height)] });
    }
  }

  if (ir.logoOverlay?.src) {
    const logo = ir.logoOverlay;
    const x = clampUnit(logo.x - 0.5);
    const y = clampUnit(0.5 - logo.y);
    tracks.push({
      clips: logoOverlaySpans(logo).map((span) => ({
        asset: { type: 'image', src: logo.src },
        start: span.start,
        length: span.length,
        fit: 'contain',
        position: 'center',
        scale: logo.scale,
        offset: { x, y },
        ...motionClipProps(logo.motion, span.length, { x, y, scale: logo.scale }),
      })),
    });
  }

  if (ir.watermark) {
    logoTrackClips.push(
      buildLogoOverlay({
        logoUrl: ir.watermark.src,
        start: ir.watermark.start,
        length: ir.watermark.length,
        scale: ir.watermark.scale,
        position: ir.watermark.position,
      }),
    );
  }

  if (partnerLogoClips.length > 0) {
    partnerLogoClips.sort((a, b) => (a.start ?? 0) - (b.start ?? 0));
    tracks.push({ clips: partnerLogoClips });
  }

  if (logoTrackClips.length > 0) {
    logoTrackClips.sort((a, b) => (a.start ?? 0) - (b.start ?? 0));
    tracks.push({ clips: logoTrackClips });
  }

  tracks.push(...packVisualTracks(mainClips));

  if (ir.audioClips && ir.audioClips.length > 0) {
    for (const audio of ir.audioClips) {
      const asset: Record<string, any> = {
        type: 'audio',
        src: audio.src,
        volume: audioVolumeTweens(audio.volume, audio.length, audio.fadeIn, audio.fadeOut),
      };
      if (audio.trim > 0) asset.trim = audio.trim;
      tracks.push({
        clips: [
          {
            asset,
            start: audio.start,
            length: audio.length,
          },
        ],
      });
    }
  } else if (ir.music) {
    tracks.push({
      clips: [
        {
          asset: {
            type: 'audio',
            src: ir.music.src,
            volume: ir.music.volume,
            effect: 'fadeInFadeOut',
          },
          start: 0,
          length: ir.totalLength,
        },
      ],
    });
  }

  return {
    timeline: {
      background: brandColor,
      tracks,
    },
    output: {
      format: 'mp4',
      resolution: 'hd',
      aspectRatio: ir.aspectRatio,
      size: { width, height },
    },
  };
}

export {
  activeVisuals,
  addAudio,
  addText,
  animatableClips,
  appendClip,
  attachAnimationJob,
  clearAnimationJob,
  isAnimatedScene,
  reconcileSceneJobs,
  revertToOriginalFootage,
  revertToStill,
  setAnimationResult,
  stillAssetId,
  applyAnimation,
  applyProcessedFootage,
  clipAtTime,
  compileEditorTimeline,
  createEditorId,
  deleteAudio,
  deleteClip,
  deleteClipsForAsset,
  deleteText,
  duplicateClip,
  editorStateFromLegacy,
  emptyEditorState,
  formatFromLegacy,
  historyCommit,
  historyInit,
  historyRedo,
  historyUndo,
  insertBlank,
  layOutClips,
  limitOverlapVolumes,
  moveClip,
  overlayMotion,
  parseEditorState,
  plateLogoMotion,
  projectDuration,
  roundTime,
  setAllClipAudio,
  copyPlateStyle,
  plateStyleOf,
  brandPlatesOf,
  parsePlateStyle,
  applyBrandPlates,
  placeSecondLogo,
  clampAudioFades,
  MAX_AUDIO_FADE_SECONDS,
  setClipAudio,
  setClipFit,
  setClipGrade,
  setClipOffset,
  setClipScale,
  clipScale,
  clipSourceEnd,
  fitClipsToSources,
  clipGap,
  placeClip,
  holdClipPositions,
  closeClipGap,
  closeAllClipGaps,
  insertSpeedZone,
  setClipSpeed,
  setClipSpeedZones,
  setClipTransition,
  setClipTrim,
  splitClip,
  updateAudio,
  updateBlank,
  updateLogo,
  updateText,
  visualDuration,
  DEFAULT_CROSSFADE_SECONDS,
  DEFAULT_PHOTO_SECONDS,
  DEFAULT_STILL_SECONDS,
  DEFAULT_PLATE_LOGO_SCALE,
  DEFAULT_VOICE_DUCK,
  MAX_OVERLAY_LOGO_SCALE,
  MAX_PLATE_LOGO_SCALE,
  MIN_CLIP_SECONDS,
  MIN_CLIP_SCALE,
  MIN_OVERLAY_LOGO_SCALE,
  MIN_PLATE_LOGO_SCALE,
  clampAudioToSource,
  musicSourceKey,
  providerAudioProblem,
} from './editor.ts';
export type {
  ActiveVisual,
  AudioRole,
  MusicProvider,
  ClipAssetHint,
  CompileIssue,
  CutStyle,
  EditorAudio,
  EditorClip,
  PlateStyle,
  EditorHistory,
  EditorLogo,
  EditorState,
  EditorText,
  FitMode,
  LaidOutClip,
  LegacyAsset,
  LegacyProjectBits,
  MediaRef,
  MixSpan,
  SceneAnimation,
  SceneJobRef,
  SceneVideoMotion,
  StillFraming,
  TextAlign,
  TextRole,
} from './editor.ts';
export {
  motionClipProps,
  motionFrame,
  motionFromLogoAnimation,
  normaliseMotion,
  DEFAULT_MOTION,
  MAX_MOTION_SECONDS,
  MIN_MOTION_SECONDS,
  MOTION_PERSONALITIES,
  MOTION_STYLES,
} from './motion.ts';
export {
  estimateTextLines,
  fontStack,
  resolveTextStyle,
  DEFAULT_TEXT_STYLE,
  MAX_TEXT_WIDTH,
  MIN_TEXT_WIDTH,
  TEXT_FONTS,
  TEXT_PRESETS,
  TEXT_SHADOW,
  TEXT_WEIGHTS,
} from './text.ts';
export type { TextFont, TextPreset, TextStyle } from './text.ts';
export {
  clampClipSpeed,
  hasSpeedChange,
  normaliseSpeedZones,
  speedAtSceneTime,
  speedCurve,
  speedOutputSeconds,
  speedSourceAt,
  speedTrimForOutput,
  windowSpeedPieces,
  coalesceSpeedPieces,
  MIN_EXPORT_PIECE_SECONDS,
  CLIP_SPEED_PRESETS,
  MAX_CLIP_SPEED,
  MIN_CLIP_SPEED,
  MIN_SPEED_ZONE_SECONDS,
  SPEED_RAMP_SECONDS,
  SPEED_ZONE_OPEN_END,
} from './speed.ts';
export type { SpeedCurvePoint, SpeedPiece, SpeedZone } from './speed.ts';
export {
  BRIGHTEN_CURVE,
  IDENTITY_CURVE,
  activeClipGrade,
  autoWhiteBalanceFromImageData,
  averagePatch,
  buildLut,
  clipGradeOrIdentity,
  curveSamples,
  gradeChannelLuts,
  gradeFilterTables,
  gradeTo1dCube,
  identityClipGrade,
  isIdentityGrade,
  normaliseClipGrade,
  normaliseCurve,
  videoSourcePoint,
  whiteBalanceForRgb,
  whiteBalanceGains,
} from './grade.ts';
export type { ClipGrade, CurveChannel, CurvePoint } from './grade.ts';
export type {
  ElementMotion,
  MotionDirection,
  MotionFrame,
  MotionPersonality,
  MotionStyle,
  MotionStyleOption,
} from './motion.ts';
